import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../Helpers/config.json";
import { LoadingGif } from "../../Utils/Loader";
import PharmacySnackbar from "../../Utils/Snackbars/SnackbarUtil";
import { Container } from "../../Utils/styledComponents";
import ReusableDashboardHeader from "../../Containers/Dasboard/ReusableDashboardHeader";
import { BackButton } from "../../Utils/FormInputs/BackButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faUsers, 
  faDollarSign, 
  faChartBar, 
  faFileExcel, 
  faFilePdf,
  faTrophy,
  faShoppingCart,
  faHandshake,
  faMoneyBillWave
} from "@fortawesome/free-solid-svg-icons";
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../Redux/Actions/SnackbarActions/SnackbarActions";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from "recharts";

const CustomerPerformanceReport = ({ actions }) => {
  const today = new Date().toISOString().slice(0, 10);
  const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString().slice(0, 10);

  const [filters, setFilters] = useState({
    start_date: firstDayOfMonth,
    end_date: today,
    search: "",
  });

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.search) params.append("search", filters.search);

      const res = await axios.get(`${config.epharmUrl}/Creport/customer_performance_report`, { params });
      setReport(res.data);
      actions.snackbarActions.successSnackbar("Customer Performance Report Loaded!");
    } catch (err) {
      actions.snackbarActions.errorSnackbar("Failed to load report");
      console.error(err);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [filters.start_date, filters.end_date, filters.search]);

  const data = report || {};
  const { summary = {}, customers = [], chart_data = {} } = data;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const formatNumber = (num) => {
    const value = Number(num || 0);
    return `UGX ${value.toLocaleString('en-US', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    })}`;
  };

  const chartData = (chart_data.labels || []).map((label, i) => ({
    name: label.length > 15 ? label.substring(0, 15) + "..." : label,
    profit: chart_data.profit?.[i] || 0,
    spent: chart_data.total_spent?.[i] || 0,
    visits: chart_data.times_bought?.[i] || 0,
  }));

  return (
    <div className="journals">
      <ReusableDashboardHeader 
        component="Customer Performance Report" 
        heading="Reports" 
        subHeading="Customer Rankings • Profit • Sales • Credit" 
        link="/customer-performance" 
      />

      <Container>
        <PharmacySnackbar />

        <div className="mb-4">
          <BackButton text="All Reports" to="/allreports" />
        </div>

        {/* FILTERS */}
        <div className="card border-0 shadow-sm mb-5">
          <div className="card-body py-4">
            <div className="row align-items-center g-4">
              <div className="col-auto">
                <label className="form-label fw-bold text-muted small">From Date</label>
                <input type="date" name="start_date" value={filters.start_date} onChange={handleChange} className="form-control" />
              </div>
              <div className="col-auto">
                <label className="form-label fw-bold text-muted small">To Date</label>
                <input type="date" name="end_date" value={filters.end_date} onChange={handleChange} className="form-control" />
              </div>
              <div className="col">
                <label className="form-label fw-bold text-muted small">Search Customer</label>
                <input
                  type="text"
                  name="search"
                  placeholder="Enter customer name..."
                  value={filters.search}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>
              <div className="col-auto d-flex align-items-end gap-3">
                <button onClick={fetchReport} disabled={loading} className="btn btn-primary btn-lg px-5">
                  {loading ? "Loading..." : "Refresh Report"}
                </button>
                <button className="btn btn-success btn-lg">
                  <FontAwesomeIcon icon={faFileExcel} className="me-2" /> Excel
                </button>
                <button className="btn btn-danger btn-lg">
                  <FontAwesomeIcon icon={faFilePdf} className="me-2" /> PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* SUMMARY CARDS — BIGGER & BETTER */}
        <div className="row g-5 mb-5">
          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-lg h-100" style={{ background: "linear-gradient(135deg, #11998e, #38ef7d)" }}>
              <div className="card-body text-white text-center py-5">
                <FontAwesomeIcon icon={faDollarSign} size="3x" className="mb-4 opacity-75" />
                <h4 className="mb-3">Total Profit Earned</h4>
                <h1 className="display-5 fw-bold">{formatNumber(summary.total_profit_earned)}</h1>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-lg h-100" style={{ background: "linear-gradient(135deg, #3498db, #2980b9)" }}>
              <div className="card-body text-white text-center py-5">
                <FontAwesomeIcon icon={faUsers} size="3x" className="mb-4 opacity-75" />
                <h4 className="mb-3">Active Customers</h4>
                <h1 className="display-5 fw-bold">{summary.total_customers || 0}</h1>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-lg h-100" style={{ background: "linear-gradient(135deg, #f39c12, #e67e22)" }}>
              <div className="card-body text-white text-center py-5">
                <FontAwesomeIcon icon={faShoppingCart} size="3x" className="mb-4 opacity-75" />
                <h4 className="mb-3">Total Transactions</h4>
                <h1 className="display-5 fw-bold">{summary.total_times_bought || 0}</h1>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-lg h-100" style={{ background: "linear-gradient(135deg, #9b59b6, #8e44ad)" }}>
              <div className="card-body text-white text-center py-5">
                <FontAwesomeIcon icon={faMoneyBillWave} size="3x" className="mb-4 opacity-75" />
                <h4 className="mb-3">Total Revenue</h4>
                <h1 className="display-5 fw-bold">{formatNumber(summary.total_spent)}</h1>
              </div>
            </div>
          </div>
        </div>

        {/* CHART */}
        {chartData.length > 0 && (
          <div className="card border-0 shadow-sm mb-5">
            <div className="card-body p-4">
              <h4 className="text-center mb-4 fw-bold text-primary">
                <FontAwesomeIcon icon={faTrophy} className="me-3" />
                Top 10 Performing Customers
              </h4>
              <ResponsiveContainer width="100%" height={450}>
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={100}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis tickFormatter={(value) => `UGX ${value.toLocaleString()}`} />
                  <Tooltip 
                    formatter={(value) => formatNumber(value)}
                    contentStyle={{ backgroundColor: "#f8f9fa", border: "1px solid #ddd" }}
                  />
                  <Legend />
                  <Bar dataKey="profit" fill="#27ae60" name="Profit Earned" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="spent" fill="#3498db" name="Total Spent" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="visits" fill="#f39c12" name="Times Bought" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* CUSTOMER RANKING TABLE */}
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-primary text-white py-3">
            <h4 className="mb-0">
              <FontAwesomeIcon icon={faTrophy} className="me-3" />
              Customer Rankings by Profit
            </h4>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5"><LoadingGif /></div>
            ) : customers.length === 0 ? (
              <div className="text-center py-5">
                <h5 className="text-muted">No customer transactions found for selected period</h5>
                <p className="text-muted">Try adjusting the date range or search term</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="text-center">#</th>
                      <th>Customer Name</th>
                      <th className="text-center">Visits</th>
                      <th className="text-end">Total Spent</th>
                      <th className="text-end">Cash Paid</th>
                      <th className="text-end">Credit Taken</th>
                      <th className="text-end">Wholesale</th>
                      <th className="text-end">Retail</th>
                      <th className="text-end">Profit Earned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((c, i) => (
                      <tr key={i} className={i < 3 ? "table-warning" : ""}>
                        <td className="text-center">
                          <span className={`badge ${i === 0 ? 'bg-gold' : i === 1 ? 'bg-silver' : i === 2 ? 'bg-bronze' : 'bg-secondary'} rounded-pill px-3 py-2`}>
                            <strong>{i + 1}</strong>
                          </span>
                        </td>
                        <td>
                          <strong className="fs-5">{c.customer_name}</strong>
                          {i < 3 && <FontAwesomeIcon icon={faTrophy} className="ms-2 text-warning" />}
                        </td>
                        <td className="text-center fw-bold fs-5">{c.times_bought}</td>
                        <td className="text-end fw-bold">{formatNumber(c.total_spent)}</td>
                        <td className="text-end text-success fw-bold">{formatNumber(c.cash_spent)}</td>
                        <td className="text-end text-warning fw-bold">{formatNumber(c.credit_taken)}</td>
                        <td className="text-end">{formatNumber(c.wholesale_sales)}</td>
                        <td className="text-end">{formatNumber(c.retail_sales)}</td>
                        <td className="text-end fw-bold text-success fs-5">{formatNumber(c.profit_earned)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) }
});

export default connect(null, mapDispatchToProps)(CustomerPerformanceReport);